import { get, isNil, mapValues } from 'lodash';
import rewardTypes from '@enums/reward-types';
import requirementTypes from '@enums/requirement-types';
import { getRegularPrice, getTierPromoPrice, getUnitPrice } from './price-calculator';
import { getTierDiscountPercent } from './discount-percent';
import { formatValidity } from './format-validity';
import {
    getTierPrimaryReward,
    getTierRequirementAmount,
    getTierFreeItems,
    getUnitsPerTrigger,
} from './mechanic-reader';

const HEX_COLOUR = /^#[0-9a-fA-F]{3,8}$/;

const DEFAULT_THEME = {
    background: '#FF6B0F',
    text: '#262020',
    accent: '#262020',
    accentText: '#FF6B0F',
    splitBackground: '#AC3558',
    splitHighlight: '#FFDF50',
};

// Theme values become inline CSS variables, so anything that is not a plain hex colour is dropped.
export const getPriceTagThemeStyle = theme => {
    const safeTheme = mapValues(DEFAULT_THEME, (fallback, key) => {
        const value = get(theme, key);
        return typeof value === 'string' && HEX_COLOUR.test(value) ? value : fallback;
    });
    return {
        '--price-tag-background': safeTheme.background,
        '--price-tag-text': safeTheme.text,
        '--price-tag-accent': safeTheme.accent,
        '--price-tag-accent-text': safeTheme.accentText,
        '--price-tag-split-background': safeTheme.splitBackground,
        '--price-tag-split-highlight': safeTheme.splitHighlight,
    };
};

const getFirstGroupProducts = ({ products, productOfferGroups }) => {
    const firstGroupId = get(productOfferGroups, '[0]._id');
    if (isNil(firstGroupId)) return [];
    return (products || []).filter(product => product.productOfferGroupId === firstGroupId);
};

const toTagProduct = product => {
    if (!product) return null;
    const { packSize, packUnit } = product;
    return {
        brand: product.brandDescription || '',
        name: product.name || '',
        size: isNil(packSize) ? '' : `${packSize}${packUnit ? ` ${packUnit}` : ''}`,
        packSize,
        packUnit,
    };
};

/**
 * Everything a price tag layout renders that is not an editable value.
 */
export function buildPriceTagContext({
    offerMechanic,
    products,
    productOfferGroups,
    startDate,
    endDate,
    moment,
    loyaltyKey,
    currencySymbol,
    useStoredPrices,
    displayValues,
}) {
    const groupProducts = getFirstGroupProducts({ products, productOfferGroups });
    const firstProduct = groupProducts[0] || null;
    const regularPrice = getRegularPrice(firstProduct);

    const tiers = get(offerMechanic, 'tiers', []).map((tier, tierIndex) => {
        const promoPrice = getTierPromoPrice({
            offerMechanic,
            tierIndex,
            product: firstProduct,
            useStoredPrices,
        });
        const reward = getTierPrimaryReward(offerMechanic, tierIndex);
        return {
            promoPrice,
            discountPercent: getTierDiscountPercent({
                offerMechanic,
                tierIndex,
                regularPrice,
                promoPrice,
            }),
            rewardType: reward ? reward.type : null,
            rewardAmount: reward ? reward.amount : null,
            buyNumber: getTierRequirementAmount(
                offerMechanic,
                tierIndex,
                requirementTypes.buyNumber
            ),
            nthItem: getTierRequirementAmount(offerMechanic, tierIndex, requirementTypes.onNthItem),
            spendAmount: getTierRequirementAmount(
                offerMechanic,
                tierIndex,
                requirementTypes.spendMoney
            ),
            freeItems: getTierFreeItems(offerMechanic, tierIndex),
        };
    });

    const purchaseLimit = get(offerMechanic, 'maximumPerPurchase', null);
    const customerLimit = get(offerMechanic, 'maximumPerCustomer', null);
    const unitsPerTrigger = getUnitsPerTrigger(offerMechanic);
    const firstTier = tiers[0] || {};
    const isFreeItemsMechanic = firstTier.rewardType === rewardTypes.getNFree;

    return {
        product: toTagProduct(firstProduct),
        isMixAndMatch: groupProducts.length > 1,
        regularPrice,
        tiers,
        unitPrice: firstProduct
            ? getUnitPrice({ price: firstTier.promoPrice, packSize: firstProduct.packSize })
            : null,
        validity: formatValidity({ moment, startDate, endDate }),
        isLoyalty: !isNil(loyaltyKey) && get(offerMechanic, 'customerAvailability') === loyaltyKey,
        purchaseLimit,
        purchaseLimitUnits: isNil(purchaseLimit) ? null : purchaseLimit * unitsPerTrigger,
        customerLimit,
        customerLimitUnits: isNil(customerLimit) ? null : customerLimit * unitsPerTrigger,
        freeItemsLimit:
            isFreeItemsMechanic && !isNil(purchaseLimit)
                ? purchaseLimit * (firstTier.freeItems || 0)
                : null,
        currencySymbol,
        description: get(offerMechanic, 'description') || '',
        displayValues: displayValues || {},
    };
}

export default { buildPriceTagContext, getPriceTagThemeStyle };
